import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, DollarSign, Clock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

const PPVRow = ({ title, content }) => {
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (!content || content.length === 0) return null;

  return (
    <div className="mb-8" data-testid="ppv-row">
      <div className="flex items-center justify-between mb-4 px-4 md:px-8">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <DollarSign className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground">$4.99 for 7-day access</p>
          </div>
        </div>
      </div>
      
      <div className="relative group">
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 bg-gradient-to-r from-background to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          data-testid="scroll-left-button"
        >
          <div className="w-10 h-10 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center hover:bg-electric/20 transition-colors">
            <ChevronLeft className="h-6 w-6 text-foreground" />
          </div>
        </button>

        {/* Content Container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide space-x-4 px-4 md:px-8 pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {content.map((item) => (
            <div key={item.id} className="flex-shrink-0 w-72 md:w-80">
              <PPVCard content={item} navigate={navigate} />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-full w-12 bg-gradient-to-l from-background to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
          data-testid="scroll-right-button"
        >
          <div className="w-10 h-10 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center hover:bg-electric/20 transition-colors">
            <ChevronRight className="h-6 w-6 text-foreground" />
          </div>
        </button>
      </div>
    </div>
  );
};

// PPV Content Card
const PPVCard = ({ content, navigate }) => {
  return (
    <div
      className="group relative cursor-pointer rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500/10 to-orange-600/10 backdrop-blur-2xl border border-amber-500/30 transition-all duration-300 hover:scale-105 hover:border-amber-400/60 hover:shadow-2xl hover:shadow-amber-500/30"
      style={{ backdropFilter: 'blur(30px) saturate(180%)' }}
      onClick={() => navigate(`/content/${content.id}`)}
      data-testid={`ppv-card-${content.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <img
          src={content.thumbnail}
          alt={content.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-amber-500/90 flex items-center justify-center backdrop-blur-sm">
              <Play className="h-7 w-7 text-background fill-background" />
            </div>
          </div>
        </div>

        {/* PPV Price Badge */}
        <div className="absolute top-3 left-3 bg-amber-500 px-3 py-1.5 rounded-lg flex items-center space-x-1 shadow-lg">
          <DollarSign className="h-4 w-4 text-background" />
          <span className="text-sm font-bold text-background">$4.99</span>
        </div>

        {/* 7-Day Access Badge */}
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center space-x-1">
          <Clock className="h-3 w-3 text-white" />
          <span className="text-xs font-medium text-white">7-Day Access</span>
        </div>

        {/* Favorite Button */}
        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <FavoriteButton contentId={content.id} size="sm" />
        </div>
      </div>

      {/* Content Info */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-foreground line-clamp-1 mb-2">
          {content.title}
        </h3>
        
        <p className="text-sm text-foreground/70 line-clamp-2 mb-3">
          {content.description}
        </p>
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-3 text-muted-foreground">
            <span>{content.year}</span>
            {content.duration && <span>{content.duration}</span>}
          </div>
          <div className="flex items-center space-x-1">
            <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
            <span className="text-foreground font-medium">{content.rating.toFixed(1)}</span>
          </div>
        </div>

        {/* Genre Tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {content.genre.slice(0, 3).map((genre, index) => (
            <span
              key={index}
              className="px-2 py-1 rounded-full bg-amber-500/20 text-xs text-amber-300 border border-amber-500/30"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PPVRow;
